import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpRequest } from '@angular/common/http';
import { Local } from '../constants';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { ResponseResult } from '../interfaces';
import { retry, catchError } from 'rxjs/operators';
import { TranslateHelpersService } from '../helpers';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private httpOptions: any = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      lang: this.translate.getLocalLang(),
      parentId: '0',
      Authorization: 'Bearer ' + localStorage.getItem(Local.TOKEN),
    }),
  };

  private httpOptionsCustom: any = {
    headers: new HttpHeaders({
      accept: 'application/json',
      lang: this.translate.getLocalLang(),
      parentId: '0',
      Authorization: 'Bearer ' + localStorage.getItem(Local.TOKEN),
      observe: 'events'
    }),
  };

  public base_url: string = environment.APIEndpoint;

  constructor(
    private http: HttpClient,
    private translate: TranslateHelpersService
  ) {}

  changeHeader(lang: string = '', parentId: string = '0') {
    this.httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        lang: lang === '' ? this.translate.getLocalLang() : lang,
        parent: parentId + '',
        Authorization: 'Bearer ' + localStorage.getItem(Local.TOKEN),
      }),
    };
  }

  getAll(
    url: string,
    hasParam: boolean,
    pageSize: number,
    pageIndex: number,
    lang: string = ''
  ): Observable<ResponseResult> {
    if (hasParam) {
      url = url + '&';
    } else {
      url = url + '?';
    }
    this.changeHeader(lang);
    return this.http
      .get<ResponseResult>(
        `${this.base_url}${url}pageSize=${pageSize}&pageIndex=${pageIndex}`,
        this.httpOptions
      )
      .pipe(retry(1), catchError(this.handleError));
  }

  getAllNoPaging(
    url: string,
    lang: string = ''
  ): Observable<ResponseResult> {
    this.changeHeader(lang);
    return this.http
      .get<ResponseResult>(
        `${this.base_url}${url}`,
        this.httpOptions
      )
      .pipe(retry(1), catchError(this.handleError));
  }

  getByUserByRole(
    url: string,
    hasParam: boolean,
    pageSize: number,
    pageIndex: number,
    lang: string = ''
  ): Observable<ResponseResult> {
    if (hasParam) {
      url = url + '&';
    } else {
      url = url + '?';
    }
    this.changeHeader(lang);
    return this.http
      .get<ResponseResult>(
        `${this.base_url}${url}pageSize=${pageSize}&pageIndex=${pageIndex}`,
        this.httpOptions
      )
      .pipe(retry(1), catchError(this.handleError));
  }
  getById(url: string, lang: string = ''): Observable<ResponseResult> {
    this.changeHeader(lang);
    return this.http
      .get<ResponseResult>(`${this.base_url}${url}`, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  createData(
    url: string,
    model: any,
    lang: string = '',
    parentId: string = '0'
  ): Observable<ResponseResult> {
    this.changeHeader(lang, parentId);
    return this.http
      .post<ResponseResult>(
        `${this.base_url}${url}`,
        JSON.stringify(model),
        this.httpOptions
      )
      .pipe(retry(1), catchError(this.handleError));
  }

  getDataByBody(
    url: string,
    model: any,
    lang: string = '',
    parentId: string = '0'
  ): Observable<ResponseResult> {
    this.changeHeader(lang, parentId);
    return this.http
      .post<ResponseResult>(
        `${this.base_url}${url}`,
        JSON.stringify(model),
        this.httpOptions
      )
      .pipe(retry(1), catchError(this.handleError));
  }

  updateData(
    url: string,
    model: any,
    lang: string = '',
    parentId: string = '0'
  ): Observable<ResponseResult> {
    this.changeHeader(lang, parentId);
    return this.http
      .post<ResponseResult>(
        `${this.base_url}${url}`,
        JSON.stringify(model),
        this.httpOptions
      )
      .pipe(retry(1), catchError(this.handleError));
  }

  deleteData(url: string): Observable<ResponseResult> {
    this.changeHeader('', '0');
    const model = {};
    return this.http
      .post<ResponseResult>(`${this.base_url}${url}/delete`, model, this.httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }

  private handleError(error: any): Promise<any> {
    return Promise.reject(error.message || error);
  }

  uploadFile(
    url: string,
    model: any,
    lang: string = '',
    parentId: string = '0'
  ): Observable<ResponseResult> {
    this.changeHeader(lang, parentId);
    return this.http
      .post<ResponseResult>(`${this.base_url}${url}`, model, this.httpOptionsCustom)
      .pipe(retry(1), catchError(this.handleError));
  }

  uploadImageFile(
    url: string,
    model: any,
    lang: string = '',
    parentId: string = '0'
  ): Observable<ResponseResult> {
    this.changeHeader(lang, parentId);
    return this.http
      .post<ResponseResult>(`${this.base_url}${url}`, model, this.httpOptionsCustom)
      .pipe(retry(1), catchError(this.handleError));
  }
  
  downloadPdfFile(url: string, lang: string = ''): Observable<any> {
    this.changeHeader(lang);
    let httpOptionsCustom: any = {
      headers: new HttpHeaders({
        'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        lang: this.translate.getLocalLang(),
        parentId: '0',
        Authorization: 'Bearer ' + localStorage.getItem(Local.TOKEN)
      }),
      observe: "response",
      responseType: "blob"
    }
    return this.http
      .get(`${this.base_url}${url}`, httpOptionsCustom).pipe(retry(1), catchError(this.handleError));
  }

  downloadExcelFileByPost(url: string, model: any, lang: string = ''): Observable<any> {
    const httpOptions: any = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        lang: this.translate.getLocalLang(),
        parentId: '0',
        Authorization: 'Bearer ' + localStorage.getItem(Local.TOKEN)
      }),
      observe: 'response',
      responseType: 'blob'
    };
    return this.http
      .post(`${this.base_url}${url}`, model, httpOptions)
      .pipe(retry(1), catchError(this.handleError));
  }
}
